import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Text,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  VStack,
  HStack,
  Divider,
} from '@chakra-ui/react';
import {
  MdPeople,
  MdWork,
  MdAttachMoney,
  MdWarning,
  MdSchool,
  MdPayment,
  MdReceipt,
  MdCalendarToday,
  MdMenuBook,
  MdAssignment,
  MdClass,
} from 'react-icons/md';
import Card from 'components/card/Card';
import {
  getGeneralStats,
  getFinancialStats,
  getAcademicStats,
  getRecentActivity,
  getEnrollmentByStudy,
} from '../../services/dashboard.service';

const currencyUy = new Intl.NumberFormat('es-UY', {
  style: 'currency',
  currency: 'UYU',
});

const dateUy = new Intl.DateTimeFormat('es-UY', {
  dateStyle: 'short',
  timeStyle: 'short',
});

function StatCard({ icon: IconComp, iconBg, label, value, helpText }) {
  const cardBg = useColorModeValue('white', 'navy.800');
  const labelColor = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const numberColor = useColorModeValue('secondaryGray.900', 'white');

  return (
    <Card p="20px" bg={cardBg}>
      <Flex align="flex-start" justify="space-between" gap={3}>
        <Stat flex="1" minW={0}>
          <StatLabel color={labelColor} fontSize="sm" fontWeight="500">
            {label}
          </StatLabel>
          <StatNumber color={numberColor} fontSize="2xl" fontWeight="700" mt={1}>
            {value}
          </StatNumber>
          {helpText != null && helpText !== '' && (
            <StatHelpText mb={0} fontSize="xs">
              {helpText}
            </StatHelpText>
          )}
        </Stat>
        <Flex
          w="48px"
          h="48px"
          align="center"
          justify="center"
          borderRadius="12px"
          bg={iconBg}
          flexShrink={0}
        >
          <Icon as={IconComp} w="24px" h="24px" color="white" />
        </Flex>
      </Flex>
    </Card>
  );
}

export default function DashboardView() {
  const [loading, setLoading] = useState(true);
  const [generalStats, setGeneralStats] = useState(null);
  const [financialStats, setFinancialStats] = useState(null);
  const [academicStats, setAcademicStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [enrollmentByStudy, setEnrollmentByStudy] = useState([]);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const cardBg = useColorModeValue('white', 'navy.800');
  const barTrackBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const barColors = useColorModeValue(
    ['blue.400', 'teal.400', 'purple.400', 'orange.400', 'pink.400', 'cyan.400'],
    ['blue.300', 'teal.300', 'purple.300', 'orange.300', 'pink.300', 'cyan.300'],
  );
  const activityIconBoxBg = useColorModeValue('gray.100', 'whiteAlpha.200');

  const maxEnrollment = useMemo(() => {
    if (!enrollmentByStudy.length) return 1;
    return Math.max(1, ...enrollmentByStudy.map((r) => Number(r.studentCount) || 0));
  }, [enrollmentByStudy]);

  useEffect(() => {
    let cancelled = false;

    const load = async (fetcher, setter, fallback) => {
      try {
        const res = await fetcher();
        if (cancelled) return;
        if (res?.status === 200 && res?.payload != null) {
          setter(res.payload);
        } else {
          setter(fallback);
        }
      } catch (e) {
        console.error('DashboardView:', e);
        if (!cancelled) setter(fallback);
      }
    };

    const run = async () => {
      setLoading(true);
      await Promise.all([
        load(getGeneralStats, setGeneralStats, null),
        load(getFinancialStats, setFinancialStats, null),
        load(getAcademicStats, setAcademicStats, null),
        load(
          () => getRecentActivity(10),
          (payload) => setRecentActivity(Array.isArray(payload) ? payload : []),
          [],
        ),
        load(
          getEnrollmentByStudy,
          (payload) => setEnrollmentByStudy(Array.isArray(payload) ? payload : []),
          [],
        ),
      ]);
      if (!cancelled) setLoading(false);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const formatActivityDate = (raw) => {
    if (raw == null || raw === '') return '—';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return dateUy.format(d);
  };

  const activityIcon = (type) => {
    if (type === 'PAYROLL') return MdReceipt;
    if (type === 'INSCRIPTION') return MdMenuBook;
    return MdPayment;
  };

  const activityLabel = (type) => {
    if (type === 'PAYROLL') return 'Nómina';
    if (type === 'INSCRIPTION') return 'Inscripción';
    return 'Pago';
  };

  const gradeDisplay =
    academicStats?.recentGradesAverageYear != null &&
    !Number.isNaN(Number(academicStats.recentGradesAverageYear))
      ? Number(academicStats.recentGradesAverageYear).toLocaleString('es-UY', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 2,
        })
      : '—';

  if (loading) return null;

  const g = generalStats ?? {};
  const f = financialStats ?? {};
  const a = academicStats ?? {};

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} px={{ base: '20px', xl: '40px' }}>
      <Text color={textColor} fontSize="2xl" fontWeight="700" mb={6}>
        Panel general
      </Text>

      <Grid
        templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }}
        gap={6}
        mb={6}
      >
        <GridItem>
          <StatCard
            icon={MdPeople}
            iconBg="blue.400"
            label="Estudiantes activos"
            value={g.totalActiveStudents ?? 0}
            helpText={`${g.studentsEnrolledThisYear ?? 0} inscriptos este año`}
          />
        </GridItem>
        <GridItem>
          <StatCard
            icon={MdSchool}
            iconBg="purple.400"
            label="Docentes activos"
            value={g.totalActiveTeachers ?? 0}
            helpText={`${g.totalActiveEmployees ?? 0} empleados en total`}
          />
        </GridItem>
        <GridItem>
          <StatCard
            icon={MdClass}
            iconBg="teal.400"
            label="Dictados activos"
            value={g.totalActiveCourses ?? 0}
            helpText={`${g.totalCourseEnrollments ?? 0} inscripciones a materias`}
          />
        </GridItem>
        <GridItem>
          <StatCard
            icon={MdAssignment}
            iconBg="orange.400"
            label="Inscripciones a exámenes"
            value={g.examInscriptionsCount ?? 0}
            helpText={`${a.upcomingExamsNext30Days ?? 0} exámenes en los próximos 30 días`}
          />
        </GridItem>
      </Grid>

      <Grid
        templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }}
        gap={6}
        mb={6}
      >
        <GridItem>
          <StatCard
            icon={MdAttachMoney}
            iconBg="green.400"
            label="Ingresos del mes"
            value={currencyUy.format(Number(f.totalIncomeThisMonth) || 0)}
          />
        </GridItem>
        <GridItem>
          <StatCard
            icon={MdWarning}
            iconBg="red.400"
            label="Deuda pendiente"
            value={currencyUy.format(Number(f.totalPendingBalance) || 0)}
          />
        </GridItem>
        <GridItem>
          <StatCard
            icon={MdReceipt}
            iconBg="cyan.400"
            label="Nómina del mes"
            value={currencyUy.format(Number(f.totalPayrollThisMonth) || 0)}
          />
        </GridItem>
        <GridItem>
          <StatCard
            icon={MdWork}
            iconBg="gray.500"
            label="Carreras y materias"
            value={`${a.studiesCount ?? 0} / ${a.subjectsCount ?? 0}`}
            helpText="Carreras activas / materias activas"
          />
        </GridItem>
      </Grid>

      <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6} mb={6}>
        <GridItem>
          <Card p="24px" bg={cardBg}>
            <Text color={textColor} fontSize="lg" fontWeight="700" mb={4}>
              Estudiantes por carrera
            </Text>
            {enrollmentByStudy.length === 0 ? (
              <Text color={mutedColor} fontSize="sm">
                No hay inscripciones a dictados registradas.
              </Text>
            ) : (
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th borderColor={borderColor} color={mutedColor}>
                      Carrera
                    </Th>
                    <Th isNumeric borderColor={borderColor} color={mutedColor}>
                      Estudiantes
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {enrollmentByStudy.map((row, idx) => {
                    const count = Number(row.studentCount) || 0;
                    const pct = (count / maxEnrollment) * 100;
                    const barColor = barColors[idx % barColors.length];
                    return (
                      <Tr key={`${row.studyName ?? 'study'}-${idx}`}>
                        <Td borderColor={borderColor} maxW="200px">
                          <Text noOfLines={2} fontSize="sm" color={textColor}>
                            {row.studyName ?? '—'}
                          </Text>
                          <Box mt={2} h="8px" borderRadius="full" bg={barTrackBg} overflow="hidden">
                            <Box h="100%" w={`${pct}%`} bg={barColor} borderRadius="full" />
                          </Box>
                        </Td>
                        <Td isNumeric borderColor={borderColor} verticalAlign="top">
                          <Text fontWeight="600" color={textColor}>
                            {count}
                          </Text>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            )}
          </Card>
        </GridItem>

        <GridItem>
          <Card p="24px" bg={cardBg}>
            <Text color={textColor} fontSize="lg" fontWeight="700" mb={4}>
              Indicadores académicos
            </Text>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <Stat>
                <HStack spacing={2} mb={1}>
                  <Icon as={MdSchool} color="blue.400" boxSize={5} />
                  <StatLabel color={mutedColor} fontSize="sm" mb={0}>
                    Carreras activas
                  </StatLabel>
                </HStack>
                <StatNumber color={textColor} fontSize="xl">
                  {a.studiesCount ?? 0}
                </StatNumber>
              </Stat>
              <Stat>
                <HStack spacing={2} mb={1}>
                  <Icon as={MdMenuBook} color="teal.400" boxSize={5} />
                  <StatLabel color={mutedColor} fontSize="sm" mb={0}>
                    Materias activas
                  </StatLabel>
                </HStack>
                <StatNumber color={textColor} fontSize="xl">
                  {a.subjectsCount ?? 0}
                </StatNumber>
              </Stat>
              <Stat>
                <HStack spacing={2} mb={1}>
                  <Icon as={MdCalendarToday} color="purple.400" boxSize={5} />
                  <StatLabel color={mutedColor} fontSize="sm" mb={0}>
                    Exámenes próximos (30 días)
                  </StatLabel>
                </HStack>
                <StatNumber color={textColor} fontSize="xl">
                  {a.upcomingExamsNext30Days ?? 0}
                </StatNumber>
              </Stat>
              <Stat>
                <StatLabel color={mutedColor} fontSize="sm">
                  Promedio de notas (año)
                </StatLabel>
                <StatNumber color={textColor} fontSize="xl">
                  {gradeDisplay}
                </StatNumber>
              </Stat>
            </Grid>
          </Card>
        </GridItem>
      </Grid>

      <Grid templateColumns={{ base: '1fr', lg: '1fr' }} gap={6} pb={8}>
        <GridItem>
          <Card p="24px" bg={cardBg}>
            <Text color={textColor} fontSize="lg" fontWeight="700" mb={4}>
              Actividad reciente
            </Text>
            {recentActivity.length === 0 ? (
              <Text color={mutedColor} fontSize="sm">
                No hay inscripciones, pagos ni liquidaciones recientes.
              </Text>
            ) : (
              <VStack align="stretch" spacing={0} divider={<Divider borderColor={borderColor} />}>
                {recentActivity.map((item, idx) => (
                  <Flex key={`${item.createdDate}-${idx}`} py={3} gap={3} align="flex-start">
                    <Flex
                      w="40px"
                      h="40px"
                      borderRadius="10px"
                      bg={activityIconBoxBg}
                      align="center"
                      justify="center"
                      flexShrink={0}
                    >
                      <Icon as={activityIcon(item.type)} boxSize={5} color={textColor} />
                    </Flex>
                    <Box flex="1" minW={0}>
                      <Text fontSize="xs" color={mutedColor} fontWeight="600" textTransform="uppercase">
                        {activityLabel(item.type)}
                      </Text>
                      <Text fontSize="sm" color={textColor} fontWeight="500" noOfLines={3}>
                        {item.description ?? '—'}
                      </Text>
                      <Text fontSize="xs" color={mutedColor} mt={1}>
                        {formatActivityDate(item.createdDate)}
                      </Text>
                    </Box>
                    <Text fontSize="sm" fontWeight="600" color={textColor} whiteSpace="nowrap">
                      {item.amount != null && item.type !== 'INSCRIPTION'
                        ? currencyUy.format(Number(item.amount) || 0)
                        : '—'}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            )}
          </Card>
        </GridItem>
      </Grid>
    </Box>
  );
}
